var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DarePlatform, Platform } from 'root/src/shared/@types/platforms';
import { faTwitch } from '@fortawesome/free-brands-svg-icons/faTwitch';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { getReadableDarePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { cva } from 'class-variance-authority';
import { SiYoutubeshorts } from 'react-icons/si';
import { FaTwitch, FaTiktok, FaYoutube } from 'react-icons/fa';
import { RiTwitterXLine } from 'react-icons/ri';
const platformIcon = {
    [Platform.Twitch]: faTwitch,
    [Platform.YouTube]: faYoutube,
    [Platform.TikTok]: faTiktok,
};
export var PlatformIconColor;
(function (PlatformIconColor) {
    PlatformIconColor["Dark"] = "dark";
    PlatformIconColor["Light"] = "light";
    PlatformIconColor["Colorized"] = "colorized";
})(PlatformIconColor || (PlatformIconColor = {}));
const platformVariant = {
    [PlatformIconColor.Colorized]: {
        [Platform.Twitch]: '*:fill-twitch-purple',
        [Platform.YouTube]: '*:fill-youtube-red',
        [Platform.TikTok]: '*:fill-tiktok-black',
    },
    [PlatformIconColor.Dark]: {
        [Platform.Twitch]: '*:fill-twitch-black',
        [Platform.YouTube]: '*:fill-youtube-black',
        [Platform.TikTok]: '*:fill-tiktok-black',
    },
    [PlatformIconColor.Light]: {
        [Platform.Twitch]: '*:fill-twitch-white',
        [Platform.YouTube]: '*:fill-youtube-white',
        [Platform.TikTok]: '*:fill-tiktok-white',
    },
};
export const PlatformIcon = memo((_a) => {
    var { platform, color = PlatformIconColor.Light, className } = _a, props = __rest(_a, ["platform", "color", "className"]);
    const icon = platformIcon[platform];
    const classVariant = platformVariant[color][platform];
    return (<FontAwesomeIcon className={twMerge(classVariant, className)} icon={icon} {...props}/>);
});
const darePlatformIcon = {
    [DarePlatform.Twitch]: params => <FaTwitch {...params}/>,
    [DarePlatform.YouTube]: params => <FaYoutube {...params}/>,
    [DarePlatform.TikTok]: params => <FaTiktok {...params}/>,
    [DarePlatform.YouTubeShorts]: params => <SiYoutubeshorts {...params}/>,
    [DarePlatform.X]: params => <RiTwitterXLine {...params}/>,
};
const darePlatformVariant = {
    [PlatformIconColor.Colorized]: {
        [DarePlatform.Twitch]: '*:fill-twitch-purple',
        [DarePlatform.YouTube]: '*:fill-youtube-red',
        [DarePlatform.TikTok]: '*:fill-tiktok-black',
        [DarePlatform.YouTubeShorts]: 'fill-youtube-white',
        [DarePlatform.X]: '*:fill-tiktok-black',
    },
    [PlatformIconColor.Dark]: {
        [DarePlatform.Twitch]: '*:fill-twitch-black',
        [DarePlatform.YouTube]: '*:fill-youtube-black',
        [DarePlatform.TikTok]: '*:fill-tiktok-black',
        [DarePlatform.YouTubeShorts]: 'fill-youtube-black',
        [DarePlatform.X]: '*:fill-tiktok-black',
    },
    [PlatformIconColor.Light]: {
        [DarePlatform.Twitch]: '*:fill-twitch-white',
        [DarePlatform.YouTube]: '*:fill-youtube-white',
        [DarePlatform.TikTok]: '*:fill-tiktok-white',
        [DarePlatform.YouTubeShorts]: 'fill-youtube-white',
        [DarePlatform.X]: '*:fill-tiktok-white',
    },
};
export const DarePlatformIcon = memo((_a) => {
    var { platform, color = PlatformIconColor.Light, className } = _a, props = __rest(_a, ["platform", "color", "className"]);
    const icon = darePlatformIcon[platform];
    const classVariant = darePlatformVariant[color][platform];
    const Icon = icon(Object.assign({ className: twMerge(classVariant, className) }, props));
    return Icon;
});
const classVariant = cva('bg-white py-1 rounded-xl text-black font-bold', {
    variants: {
        theme: {
            primary: 'text-white bg-primary',
            light: 'text-black bg-white',
        },
    },
});
export const PlatformIconTooltip = memo((props) => {
    const { platform, children, variant = 'primary' } = props;
    const tooltipClass = classVariant({ theme: variant });
    return (<BaseTooltip tooltip={(<BaseTooltipContent className={tooltipClass}>
					{getReadableDarePlatform(platform)}
				</BaseTooltipContent>)}>
			{children}
		</BaseTooltip>);
});
export const DarePlatformIconTooltip = memo((props) => {
    const { platform, children, variant = 'primary' } = props;
    const tooltipClass = classVariant({ theme: variant });
    return (<BaseTooltip tooltip={(<BaseTooltipContent className={tooltipClass}>
					{getReadableDarePlatform(platform)}
				</BaseTooltipContent>)}>
			{children}
		</BaseTooltip>);
});
